/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import * as Highcharts from 'highcharts';

import template from './stats-widget.component.html';

import {StatsResource} from '../../../components/api/resources/resource/stats-resources/stats-resource';
import {CHART_COLORS} from '../../common/options/chart-colors';
import {ADN_CHART_COUNT_TYPES} from '../../common/options/chart-options';
import {OBJECT_TYPE} from '../../common/constants/object-type';
import {applyHighchartsLocale, chunkYValue} from './workspace-widget-chart-commons';
import {formatLocaleDate} from '../../common/locale/locale-date-format';
import {getIntlLocaleData} from '../../common/locale/intl-locale-data';
import {LocaleHelper} from '../../../components/util/locale-helper';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {ChartOptionsUtil} from '../../common/options/chart-options-util.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-stats-widget',
  template
})
export class StatsWidgetComponent implements OnInit, OnDestroy {
  @Input() widgetType: any;

  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  chartTitle = '';
  error = false;
  isPending = false;
  haveStats = false;
  noData: string = null;
  countType = ADN_CHART_COUNT_TYPES.singular;

  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptions: any = null;
  private networkTimezoneOffset = 0;

  constructor(
    private readonly statsResource: StatsResource,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    private readonly translate: TranslateService,
    private readonly adnLog: AdnLogService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    const timezone = this.LocalNetworkProfile.get('timeZone');
    this.networkTimezoneOffset = moment.tz(timezone).utcOffset();

    let dateOption = this.ChartOptionsUtil.getDateOptions().last7Days;
    if (this.widgetType.dateOption && this.ChartOptionsUtil.getDateOptions()[this.widgetType.dateOption]) {
      dateOption = this.ChartOptionsUtil.getDateOptions()[this.widgetType.dateOption];
    }
    let startDate = dateOption.startDate();
    let endDate = dateOption.endDate();

    const networkTrans = this.LocalNetworkProfile.getNetworkTranslations(this.widgetType.selField);
    this.chartTitle = (networkTrans || this.translate.instant('report.field.' + this.widgetType.selField))
      + ' for ' + this.translate.instant('chart.dateOption.' + dateOption.id + '.sentence');
    if (this.widgetType.dateOption === 'custom') {
      startDate = this.widgetType.dateOptionStartDate || startDate;
      endDate = this.widgetType.dateOptionEndDate || endDate;
      const locale = LocaleHelper.toAngularFormat(this.localUserProfile.get('locale') || 'en_GB');
      const timeZone = this.LocalNetworkProfile.get('timeZone') || 'utc';
      this.chartTitle = (networkTrans || this.translate.instant('report.field.' + this.widgetType.selField))
        + ': ' + formatLocaleDate(startDate, 'short', locale, timeZone)
        + ' - ' + formatLocaleDate(endDate, 'short', locale, timeZone);
    }

    const objType = OBJECT_TYPE[this.widgetType.objectType] || OBJECT_TYPE.LineItem;
    const chartType = this.ChartOptionsUtil.getOptions(objType.caps);

    let statsSeriesIds = _.cloneDeep(this.widgetType.objIds);
    const totalsId = {id: 'totals', name: 'Totals'};
    if (this.widgetType.totalsType === 'SHOW') {
      statsSeriesIds.push(totalsId);
    } else if (this.widgetType.totalsType === 'SHOW_ONLY') {
      statsSeriesIds = [totalsId];
    }

    const ids = _.map(this.widgetType.objIds, 'id');
    if (!ids.length) {
      this.isPending = false;
      this.noData = 'Select at least one object to report stats on.';
      return;
    }

    const series = _.map(statsSeriesIds, (c) => ({
      data: [],
      id: c.id,
      name: c.name,
      yAxis: 0
    }));

    this.chartOptions = {
      chart: {
        type: 'line',
        zoomType: 'x',
        showAxes: true,
        height: this.widgetType.height || 300
      },
      plotOptions: {
        series: {
          marker: {enabled: false},
          turboThreshold: 2000
        }
      },
      tooltip: {shared: true},
      xAxis: {
        type: 'datetime',
        title: {enabled: false, text: 'Date and Time'}
      },
      yAxis: {
        title: {text: null},
        min: 0,
        floor: 0,
        opposite: false,
        id: 0
      },
      series,
      title: {text: null},
      credits: {enabled: false},
      accessibility: {enabled: false},
      exporting: {enabled: true}
    };

    const getCollDataPoint = (theChunks: any, objectId: string) => {
      if (objectId === 'totals') {
        return _.get(theChunks, ['totals', this.widgetType.selField]) || 0;
      }
      let objParam = 'creative';
      if (chartType.idKey === 'lineItemId') {
        objParam = 'lineItem';
      } else if (chartType.idKey === 'adUnitId') {
        objParam = 'adUnit';
      } else if (chartType.idKey === 'orderId') {
        objParam = 'order';
      } else if (chartType.idKey === 'siteId') {
        objParam = 'site';
      } else if (chartType.idKey === 'earningsAccountId') {
        objParam = 'earningsAccount';
      }
      const object = _.find(theChunks, (c) => c[objParam].id === objectId);
      return _.get(object, this.widgetType.selField) || 0;
    };

    const chunkSize = this.ChartOptionsUtil.getChunkSize(startDate, endDate);
    const statsParams: any = {
      id: _.slice(ids, 0, 50).join(';'),
      idKey: chartType.idKey,
      startDate,
      endDate,
      groupBy: chunkSize.key,
      timeout: 60000,
      aggregateTo: objType.caps
    };

    const field = _.find(chartType.fields, ['id', this.widgetType.selField]);
    _.forEach(this.chartOptions.series, (s: any) => {
      s.isPercentage = field.isPercentage;
      s.allowDecimals = field.allowDecimals;
      s.tooltip = field.tooltip;
    });

    this.statsResource.get(statsParams, (stats: any) => {
      applyHighchartsLocale(getIntlLocaleData(LocaleHelper.toAngularFormat(this.localUserProfile.get('locale') || 'en_GB')), this.networkTimezoneOffset);

      const keyedChunks = _.keyBy(stats.chunks, 'chunkStart');
      _.forEach(this.chartOptions.series, (s: any, loopCounter: number) => {
        s.yAxis = 0;
        s.color = CHART_COLORS[loopCounter % CHART_COLORS.length];

        let runningTotal = 0;
        s.data = _.map(stats.chunkRange, (statsDate: string) => {
          let dataPoint = null;
          if (keyedChunks[statsDate]) {
            const theValue = s.id === 'totals' ? keyedChunks[statsDate] : keyedChunks[statsDate].chunks;
            dataPoint = getCollDataPoint(theValue, s.id);
          }
          let value = chunkYValue(dataPoint) || 0;
          if (s.isPercentage) {
            value = value * 100;
            runningTotal = value;
          } else {
            runningTotal += value;
          }
          return {
            x: moment(statsDate).valueOf(),
            y: this.countType === ADN_CHART_COUNT_TYPES.cumulative && !s.singularOnly ? runningTotal : value
          };
        });
      });

      const noData = !stats.chunks.length
        ? 'No data was found for your selected '
          + this.translate.instant('object.' + this.widgetType.objectType + '.plural').toLowerCase()
        : null;

      deferMarkForCheck(this.cdr, () => {
        this.error = false;
        this.noData = noData;
        this.haveStats = true;
        this.isPending = false;
        this.renderChart();
      });
    }, (data: any) => {
      this.adnLog.warn(data);
      deferMarkForCheck(this.cdr, () => {
        if (data.status !== -1) {
          this.error = true;
        }
        this.isPending = false;
      });
    }, this);
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
    this.chart = null;
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions || !this.haveStats || this.noData) {
      return;
    }
    this.chart?.destroy();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
  }
}
